import { useEffect, useState } from 'react';
import { Check, CheckCircle2, Clock, Eye, History, PenLine, RotateCcw, X } from 'lucide-react';
import { Modale } from '../ui/Modale';
import {
  BoutonMint,
  BoutonSecondaire,
  Chargement,
  MessageErreur,
  Pastille,
} from '../ui/Primitives';
import { ErreurApi } from '../../lib/api';
import {
  lireSeance,
  useDetailPresence,
  useRequalifierPresence,
  useSignaturePresence,
  type EvenementHistorique,
  type InstantanePresence,
  type PresenceAffichee,
  type SeanceAffichee,
} from '../../lib/useEmargement';
import { dureeLisible, heureReunion, horodatageReunion } from '../../lib/emargement-affichage';
import { useDemanderModification } from '../../lib/useModificationsSensibles';
import { useToast } from '../ui/useToast';
import {
  AvisValidation,
  ChampMotif,
  DemandeEnCours,
  motifValide,
  useValidationDirecte,
} from '../modifications-sensibles/ElementsDemande';
import { ReouvertureFeuille } from './ReouvertureFeuille';

/**
 * Détail d'un apprenant sur une demi-journée d'émargement.
 *
 * Il distingue ce que la feuille mélangeait : la **signature** — preuve qu'une
 * personne a émargé à un instant donné — et le **temps réel de présence**, que
 * disent les heures d'arrivée et de départ. Les durées viennent de l'API ; la
 * fenêtre ne recalcule rien.
 *
 * Trois gestes : voir la signature (chargée à la demande, consultation tracée
 * côté serveur), corriger la présence, rouvrir la feuille si elle est clôturée.
 * Aucune correction n'efface la signature, et chacune part à l'historique, qui
 * est affiché en dessous.
 *
 * Sur une feuille clôturée, corriger et rouvrir sont des modifications
 * sensibles (TF-437) : les autres les demandent à la direction, motif à
 * l'appui, et rien ne change avant sa décision ; la direction les fait
 * elle-même, et le pôle pédagogique corrige une présence comme elle (TF-476,
 * `seance.correctionDirecte`, dit par l'API).
 */

type StatutSaisi = 'PRESENT' | 'RETARD' | 'ABSENT';

const CHOIX: { statut: StatutSaisi; libelle: string; icone: typeof Check; ton: string }[] = [
  { statut: 'PRESENT', libelle: 'Présent', icone: Check, ton: 'bg-codialis-mint text-codialis-navy' },
  { statut: 'RETARD', libelle: 'Retard', icone: Clock, ton: 'bg-amber-400 text-codialis-navy' },
  { statut: 'ABSENT', libelle: 'Absent', icone: X, ton: 'bg-red-500 text-white' },
];

const CHAMP =
  'min-h-[44px] w-full min-w-0 rounded-xl border border-gray-200 bg-white px-3 text-sm text-codialis-navy';

function messageErreur(erreur: unknown, defaut: string): string | null {
  if (erreur === null || erreur === undefined) return null;
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

function statutSaisi(presence: PresenceAffichee): StatutSaisi {
  return presence.statut === 'ABSENT' ? 'ABSENT' : presence.statut === 'RETARD' ? 'RETARD' : 'PRESENT';
}

function Ligne({ libelle, children }: { libelle: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex min-w-0 flex-wrap justify-between gap-x-4 gap-y-0.5 border-b border-gray-100 py-2 text-sm last:border-b-0">
      <dt className="text-codialis-gray">{libelle}</dt>
      <dd className="min-w-0 text-right font-semibold text-codialis-navy">{children}</dd>
    </div>
  );
}

/** « Absent → Retard · arrivée 10:00 » : une transition lisible sans connaître le modèle. */
function resumeInstantane(valeur: InstantanePresence | null): string {
  if (!valeur) return '—';
  const libelles: Record<string, string> = {
    ATTENDU: 'Attendu',
    PRESENT: 'Présent',
    RETARD: 'Retard',
    ABSENT: 'Absent',
  };
  const morceaux = [libelles[valeur.statut] ?? valeur.statut];
  if (valeur.heureArrivee) morceaux.push(`arrivée ${valeur.heureArrivee}`);
  if (valeur.heureDepart) morceaux.push(`départ ${valeur.heureDepart}`);
  if (valeur.justifiee) morceaux.push('justifié');
  if (valeur.motifJustification) morceaux.push(`« ${valeur.motifJustification} »`);
  return morceaux.join(' · ');
}

function Evenement({ evenement }: { evenement: EvenementHistorique }): JSX.Element {
  const transition =
    evenement.ancienneValeur || evenement.nouvelleValeur
      ? `${resumeInstantane(evenement.ancienneValeur)} → ${resumeInstantane(evenement.nouvelleValeur)}`
      : evenement.presenceId === null
        ? `Feuille : ${evenement.actionLibelle.toLowerCase()}`
        : null;
  return (
    <li className="rounded-xl bg-codialis-sand px-3 py-2 text-xs text-codialis-navy">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
        <span className="font-semibold">{evenement.actionLibelle}</span>
        <span className="text-codialis-gray">
          {horodatageReunion(evenement.survenuLe)}
          {evenement.auteur ? ` · ${evenement.auteur}` : ''}
        </span>
      </div>
      {transition && <p className="mt-1 break-words">{transition}</p>}
      {evenement.motif && (
        <p className="mt-1 break-words text-codialis-gray">Motif : {evenement.motif}</p>
      )}
    </li>
  );
}

export function ModaleDetailPresence({
  seance,
  presenceId,
  onFermer,
  onSeanceMiseAJour,
}: {
  seance: SeanceAffichee;
  presenceId: string | null;
  onFermer: () => void;
  /** Rendu de la feuille après correction ou réouverture. */
  onSeanceMiseAJour: (seance: SeanceAffichee) => void;
}): JSX.Element {
  const ouverte = presenceId !== null;
  const detail = useDetailPresence(ouverte ? seance.id : null, presenceId);
  const [voirSignature, setVoirSignature] = useState(false);
  const signature = useSignaturePresence(seance.id, presenceId, ouverte && voirSignature);
  const requalifier = useRequalifierPresence();
  const demander = useDemanderModification();
  const directe = useValidationDirecte();
  const correctionDirecte = seance.correctionDirecte;
  const toast = useToast();

  const [edition, setEdition] = useState(false);
  const [reouverture, setReouverture] = useState(false);
  const [motifDemande, setMotifDemande] = useState('');
  const [statut, setStatut] = useState<StatutSaisi>('PRESENT');
  const [arrivee, setArrivee] = useState('');
  const [depart, setDepart] = useState('');
  const [justifiee, setJustifiee] = useState(false);
  const [motif, setMotif] = useState('');
  const [commentaire, setCommentaire] = useState('');

  // Changer d'apprenant repart d'une fenêtre fermée : ni la signature ni le
  // formulaire du précédent ne doivent rester affichés.
  useEffect(() => {
    setVoirSignature(false);
    setEdition(false);
    setReouverture(false);
    setMotifDemande('');
    requalifier.reset();
    demander.reset();
    // Les mutations sont stables ; on ne réinitialise qu'au changement de ligne.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presenceId]);

  const presence = detail.data?.presence ?? seance.presences.find((ligne) => ligne.id === presenceId);
  const cloturee = (detail.data?.seance.statut ?? seance.statut) === 'CLOTUREE';
  const reouvertureEnAttente = detail.data?.seance.reouvertureEnAttente ?? seance.reouvertureEnAttente;
  const demandeEnAttente = presence?.demandeEnAttente ?? null;

  /** La feuille relue après une demande, qui ne la rend pas. */
  async function relire(): Promise<void> {
    onSeanceMiseAJour(await lireSeance(seance.id));
  }

  function commencerEdition(): void {
    if (!presence) return;
    setStatut(statutSaisi(presence));
    setArrivee(presence.heureArrivee ?? '');
    setDepart(presence.heureDepart ?? '');
    setJustifiee(presence.justifiee);
    setMotif(presence.motifJustification ?? '');
    setCommentaire('');
    setMotifDemande('');
    requalifier.reset();
    demander.reset();
    setEdition(true);
  }

  function enregistrer(): void {
    if (!presenceId) return;
    if (cloturee) {
      demander.mutate(
        {
          type: 'PRESENCE',
          presenceId,
          statut,
          ...(statut === 'ABSENT' ? {} : { heureArrivee: arrivee || null, heureDepart: depart || null }),
          justifiee,
          motifJustification: motif.trim() || null,
          motif: motifDemande.trim(),
        },
        {
          onSuccess: (resultat) => {
            toast(resultat.message);
            setEdition(false);
            void relire();
          },
        },
      );
      return;
    }
    requalifier.mutate(
      {
        seanceId: seance.id,
        presenceId,
        statut,
        ...(statut === 'ABSENT'
          ? {}
          : { heureArrivee: arrivee || null, heureDepart: depart || null }),
        justifiee,
        motifJustification: motif.trim() || null,
        ...(commentaire.trim() ? { commentaire: commentaire.trim() } : {}),
      },
      {
        onSuccess: (misAJour) => {
          onSeanceMiseAJour(misAJour);
          setEdition(false);
        },
      },
    );
  }

  const dateLisible = new Date(seance.date).toLocaleDateString('fr-FR', {
    timeZone: 'Indian/Reunion',
  });

  return (
    <Modale
      ouverte={ouverte}
      titre={presence?.apprenant ?? 'Émargement'}
      sousTitre={`${seance.demiJourneeLibelle} du ${dateLisible}`}
      onFermer={onFermer}
    >
      <div className="space-y-4 p-4 sm:p-6">
        {!presence && detail.isLoading && <Chargement libelle="Chargement du détail…" />}
        {detail.error !== null && detail.error !== undefined && (
          <MessageErreur message={messageErreur(detail.error, 'Le détail n’a pas pu être chargé.')!} />
        )}

        {presence && (
          <>
            <dl>
              <Ligne libelle="Formation">{seance.formation ?? seance.libelle}</Ligne>
              {seance.session && <Ligne libelle="Session">{seance.session}</Ligne>}
              <Ligne libelle="Date">{dateLisible}</Ligne>
              <Ligne libelle="Période">{seance.demiJourneeLibelle}</Ligne>
              <Ligne libelle="Créneau prévu">
                {seance.heureDebut} → {seance.heureFin}
              </Ligne>
              <Ligne libelle={presence.releve ? 'Émargement' : 'Signature'}>
                {presence.signeLe ? (
                  <span className="inline-flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4 text-codialis-mint" aria-hidden="true" />
                    {presence.releve
                      ? `Émargé dans la salle à ${heureReunion(presence.signeLe)}, caméra allumée`
                      : `Signé à ${heureReunion(presence.signeLe)}`}
                  </span>
                ) : presence.releve ? (
                  'Non émargé'
                ) : (
                  'Non signée'
                )}
              </Ligne>
              {presence.releve && (
                <Ligne libelle="Relevé de la salle">{presence.releve.resume}</Ligne>
              )}
              <Ligne libelle="Statut">
                <Pastille texte={presence.qualificationLibelle} ton={presence.qualificationTon} />
              </Ligne>
              {presence.statut !== 'ABSENT' && presence.statut !== 'ATTENDU' && (
                <>
                  <Ligne libelle="Arrivée réelle">
                    {presence.heureArrivee ??
                      (presence.statut === 'RETARD' ? 'Non renseignée' : seance.heureDebut)}
                  </Ligne>
                  <Ligne libelle="Départ réel">{presence.heureDepart ?? seance.heureFin}</Ligne>
                  {presence.minutesRetard !== 0 && (
                    <Ligne libelle="Retard">
                      {presence.minutesRetard === null
                        ? 'Durée non renseignée'
                        : dureeLisible(presence.minutesRetard)}
                    </Ligne>
                  )}
                  {presence.minutesDepartAnticipe > 0 && (
                    <Ligne libelle="Départ anticipé">
                      {dureeLisible(presence.minutesDepartAnticipe)} avant la fin
                    </Ligne>
                  )}
                </>
              )}
              <Ligne libelle="Présence réelle">
                {presence.minutesPresence === null
                  ? 'Pas encore pointé'
                  : `${dureeLisible(presence.minutesPresence)} sur ${dureeLisible(presence.minutesPrevues)}`}
              </Ligne>
              {presence.motifJustification && (
                <Ligne libelle={presence.justifiee ? 'Motif (justifié)' : 'Motif'}>
                  {presence.motifJustification}
                </Ligne>
              )}
            </dl>

            <div className="flex flex-wrap gap-2">
              {presence.signatureDisponible && (
                <BoutonSecondaire onClick={() => setVoirSignature((visible) => !visible)}>
                  <Eye className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  {voirSignature ? 'Masquer la signature' : 'Voir la signature'}
                </BoutonSecondaire>
              )}
              {/* Ouverte, la feuille se corrige directement, demande en attente ou non. */}
              {!edition && (!cloturee || !demandeEnAttente) && (
                <BoutonSecondaire onClick={commencerEdition}>
                  <PenLine className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  {!cloturee
                    ? 'Modifier la présence'
                    : correctionDirecte
                      ? 'Corriger la présence'
                      : 'Demander une correction'}
                </BoutonSecondaire>
              )}
              {cloturee && !reouverture && !edition && (
                <BoutonSecondaire onClick={() => setReouverture(true)}>
                  <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  {reouvertureEnAttente
                    ? 'Réouverture demandée'
                    : directe
                      ? 'Rouvrir l’émargement'
                      : 'Demander la réouverture'}
                </BoutonSecondaire>
              )}
            </div>

            {demandeEnAttente && <DemandeEnCours demande={demandeEnAttente} onRetiree={() => void relire()} />}

            {voirSignature && (
              <div className="rounded-2xl border border-gray-100 bg-white p-3">
                {signature.isLoading && <Chargement libelle="Chargement de la signature…" />}
                {signature.error !== null && signature.error !== undefined && (
                  <MessageErreur
                    message={messageErreur(signature.error, 'La signature n’a pas pu être chargée.')!}
                  />
                )}
                {signature.data && (
                  <figure className="space-y-2">
                    <img
                      src={signature.data.image}
                      alt={`Signature de ${presence.apprenant}`}
                      className="mx-auto max-h-56 w-full rounded-xl bg-codialis-sand object-contain"
                    />
                    <figcaption className="break-words text-xs text-codialis-gray">
                      {presence.apprenant} · {seance.demiJourneeLibelle} du {dateLisible} · signée
                      le {horodatageReunion(signature.data.signeLe)}
                      {signature.data.empreinte
                        ? ` · empreinte ${signature.data.empreinte.slice(0, 16)}…`
                        : ''}
                    </figcaption>
                  </figure>
                )}
              </div>
            )}

            {reouverture && (
              <div className="rounded-2xl border border-gray-100 bg-white p-3 sm:p-4">
                <ReouvertureFeuille
                  seance={{ id: seance.id, reouvertureEnAttente }}
                  onAnnuler={() => setReouverture(false)}
                  onTermine={(misAJour, message) => {
                    toast(message);
                    onSeanceMiseAJour(misAJour);
                    setReouverture(false);
                  }}
                />
              </div>
            )}

            {edition && (
              <div className="space-y-3 rounded-2xl border border-gray-100 bg-white p-3 sm:p-4">
                <div className="flex gap-2" role="group" aria-label="Présence">
                  {CHOIX.map((choix) => {
                    const actif = statut === choix.statut;
                    const Icone = choix.icone;
                    return (
                      <button
                        key={choix.statut}
                        type="button"
                        aria-pressed={actif}
                        onClick={() => setStatut(choix.statut)}
                        className={`inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-xl px-3 text-sm font-semibold transition [@media(pointer:coarse)]:min-h-[48px] ${
                          actif
                            ? choix.ton
                            : 'border border-gray-200 bg-white text-codialis-gray hover:bg-gray-50'
                        }`}
                      >
                        <Icone className="h-4 w-4" aria-hidden="true" />
                        {choix.libelle}
                      </button>
                    );
                  })}
                </div>

                {statut !== 'ABSENT' && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="min-w-0 text-xs font-semibold text-codialis-gray">
                      Arrivée réelle
                      <input
                        type="time"
                        value={arrivee}
                        min={seance.heureDebut}
                        max={seance.heureFin}
                        onChange={(evenement) => setArrivee(evenement.target.value)}
                        className={`${CHAMP} mt-1`}
                      />
                      <span className="mt-1 block font-normal">
                        Vide : arrivée à {seance.heureDebut}. Une arrivée après le début est un
                        retard.
                      </span>
                    </label>
                    <label className="min-w-0 text-xs font-semibold text-codialis-gray">
                      Départ réel
                      <input
                        type="time"
                        value={depart}
                        min={seance.heureDebut}
                        max={seance.heureFin}
                        onChange={(evenement) => setDepart(evenement.target.value)}
                        className={`${CHAMP} mt-1`}
                      />
                      <span className="mt-1 block font-normal">
                        Vide : départ à {seance.heureFin}. Plus tôt, c’est un départ anticipé.
                      </span>
                    </label>
                  </div>
                )}

                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <label className="inline-flex min-h-[44px] shrink-0 items-center gap-2 text-sm text-codialis-navy">
                    <input
                      type="checkbox"
                      className="h-5 w-5 rounded"
                      checked={justifiee}
                      onChange={(evenement) => setJustifiee(evenement.target.checked)}
                    />
                    Justifié
                  </label>
                  <input
                    type="text"
                    value={motif}
                    onChange={(evenement) => setMotif(evenement.target.value)}
                    placeholder="Motif (maladie, transport, rendez-vous médical…)"
                    maxLength={500}
                    aria-label="Motif de l’absence, du retard ou du départ"
                    className={CHAMP}
                  />
                </div>
                {cloturee ? (
                  <>
                    <ChampMotif valeur={motifDemande} onChange={setMotifDemande} directe={correctionDirecte} />
                    <AvisValidation directe={correctionDirecte} parLePole={!directe} />
                  </>
                ) : (
                  <input
                    type="text"
                    value={commentaire}
                    onChange={(evenement) => setCommentaire(evenement.target.value)}
                    placeholder="Commentaire de la correction, conservé à l’historique (facultatif)"
                    maxLength={500}
                    aria-label="Commentaire de la correction"
                    className={CHAMP}
                  />
                )}
                {presence.signeLe && (
                  <p className="text-xs text-codialis-gray">
                    La signature de {heureReunion(presence.signeLe)} est conservée : seules les
                    données de présence changent.
                  </p>
                )}
                {requalifier.error !== null && (
                  <MessageErreur
                    message={messageErreur(requalifier.error, 'La correction a été refusée.')!}
                  />
                )}
                {demander.error !== null && (
                  <MessageErreur message={messageErreur(demander.error, 'La demande a été refusée.')!} />
                )}
                <div className="flex flex-wrap gap-2">
                  <BoutonMint
                    onClick={enregistrer}
                    disabled={requalifier.isPending || demander.isPending || (cloturee && !motifValide(motifDemande))}
                  >
                    {requalifier.isPending || demander.isPending
                      ? 'Enregistrement…'
                      : !cloturee
                        ? 'Enregistrer la correction'
                        : correctionDirecte
                          ? 'Appliquer la correction'
                          : 'Envoyer la demande'}
                  </BoutonMint>
                  <BoutonSecondaire onClick={() => setEdition(false)}>Annuler</BoutonSecondaire>
                </div>
              </div>
            )}

            {detail.data && detail.data.historique.length > 0 && (
              <section className="space-y-2">
                <h3 className="inline-flex items-center gap-1.5 text-sm font-semibold text-codialis-navy">
                  <History className="h-4 w-4" aria-hidden="true" />
                  Historique
                </h3>
                <ul className="space-y-1.5">
                  {detail.data.historique.map((evenement) => (
                    <Evenement key={evenement.id} evenement={evenement} />
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </div>
    </Modale>
  );
}
